import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, unref as _unref, resolveComponent as _resolveComponent, withModifiers as _withModifiers, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, createSlots as _createSlots, normalizeClass as _normalizeClass, mergeProps as _mergeProps, createElementBlock as _createElementBlock, resolveDirective as _resolveDirective, withDirectives as _withDirectives, Transition as _Transition, Fragment as _Fragment } from "vue"

const _hoisted_1 = { key: 1 }
const _hoisted_2 = { class: "d-flex justify-center mb-4 text-h6" }
const _hoisted_3 = { class: "d-flex justify-center" }
const _hoisted_4 = { class: "text-secondary" }
const _hoisted_5 = { class: "d-flex justify-space-between" }

import { getCurrentInstance, computed, ref } from "vue";
import { DialogAction } from "@/services/dialogAction";
import { useDisplay } from "vuetify";

interface DialogProps {
    /** The main title of the dialog */
    title?: string;
    /** The title displayed for the validation step */
    validTitle?: string;
    /** should the dialog be sized fullscreen?  */
    fullscreen?: boolean;
    /** class to apply to validation elements */
    validClass?: string;

    /** is the overlay to be hidden? */
    hideOverlay?: boolean;

    width?: string;
    height?: string;
    maxWidth?: string;
    minWidth?: string;
    minHeight?: string;
    maxHeight?: string;

    /** is validation disabled? */
    validDisabled?: boolean;
    /** validation buttons text */
    validText?: string;

    /** Indicates if the dialog is in a loading state */
    loading?: boolean;
    /** Actions available in the dialog */
    actions?: { action: DialogAction };

    /** Disables the 'previous' button if true */
    previousDisabled?: boolean;
    /** Disables the 'after' button if true */
    afterDisabled?: boolean;

    /** Shows the 'previous' button if true */
    previousDisplay?: boolean;
    /** Shows the 'next' button if true */
    nextDisplay?: boolean;

    /** Hides the validation step if true */
    hideValid?: boolean;

    withoutHeader?: boolean;

    validLoading?: boolean;

    prefixDataCy?: string;

    persistent?: boolean;

    error?: boolean; // TO USE => ROMAIN
    /** the return value that this ossDialog provides */
    returnValue?: any;
    transition?: {
        type: string;
        default: "dialog-transition"; // Fallback transition
    };
    bgColor?: string;

    eager?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ossDialog',
  props: /*@__PURE__*/_mergeModels({
    title: {},
    validTitle: {},
    fullscreen: { type: Boolean },
    validClass: {},
    hideOverlay: { type: Boolean },
    width: {},
    height: {},
    maxWidth: {},
    minWidth: {},
    minHeight: {},
    maxHeight: {},
    validDisabled: { type: Boolean },
    validText: {},
    loading: { type: Boolean },
    actions: {},
    previousDisabled: { type: Boolean },
    afterDisabled: { type: Boolean },
    previousDisplay: { type: Boolean },
    nextDisplay: { type: Boolean },
    hideValid: { type: Boolean },
    withoutHeader: { type: Boolean },
    validLoading: { type: Boolean },
    prefixDataCy: {},
    persistent: { type: Boolean },
    error: { type: Boolean },
    returnValue: {},
    transition: {},
    bgColor: {},
    eager: { type: Boolean }
  }, {
    "modelValue": { type: Boolean, ...{
    default: false,
} },
    "modelModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["close", "valid", "previous", "after", "afterEnter"], ["update:modelValue"]),
  setup(__props: any, { emit: __emit }) {

const emits = __emit;

const currentInstance = getCurrentInstance();

const { mobile } = useDisplay();

const props = __props;

const dialog = _useModel<boolean>(__props, "modelValue");

const snackbarDialog = ref<boolean>(false);

const _dialog = computed({
    get: () => dialog.value && !snackbarDialog.value,
    set: (value: boolean) => {
        dialog.value = value;
        snackbarDialog.value = false;
    },
});

const localLoading = ref<boolean>(false);

const $props = computed<Record<string, unknown>>(() => currentInstance?.vnode?.props || {});

const shouldEmitValidation = computed<boolean>(() => !!$props.value.onValid);
const shouldEmitPrevious = computed<boolean>(() => !!$props.value.onPrevious);
const shouldEmitAfter = computed(() => props.afterDisabled);

const hasFunctionInActions = computed(
    () =>
        !!props.actions?.action?.function &&
        props.actions?.action?.function instanceof Function &&
        props.actions?.action?.display,
);
const hasPreviousInActions = computed(() => props.actions?.action?.previous);

const hasValidButton = computed(() => {
    return (shouldEmitValidation.value || hasFunctionInActions.value) && !props.hideValid;
});
const hasPreviousButton = computed(() => {
    return (
        (shouldEmitPrevious.value || hasPreviousInActions.value) &&
        (props.previousDisplay === undefined || props.previousDisplay === true)
    );
});
const hasAfterButton = computed(() => shouldEmitAfter.value || props.nextDisplay !== false);

const loading = computed(() => {
    return props.loading || localLoading.value || props.actions?.action.loading;
});

const eager = computed(() => {
    return props.eager || false;
});

const dialogTitle = computed((): string => {
    if (props.actions?.action?.title && props.actions?.action?.title instanceof Function) {
        return props.actions?.action.title();
    }
    return props.title || "";
});
const dialogActionTitle = computed((): string => {
    if (props.actions?.action?.title) {
        if (typeof props.actions?.action.title === "function") {
            return props.actions?.action.title();
        }
        return props.actions?.action.title;
    }

    return props.validTitle || "Valider";
});

const dialogActionDisabled = computed(() => {
    if (props.actions?.action?.disabled && props.actions?.action?.disabled instanceof Function) {
        return props.actions?.action.disabled();
    }
    return props.actions?.action?.disabled || false;
});

const dialogDisabled = computed(() => {
    return props.validDisabled || dialogActionDisabled.value;
});

const validEvent = async () => {
    if (props.actions?.action?.function && props.actions?.action?.function instanceof Function) {
        localLoading.value = true;
        try {
            await props.actions?.action.function();
        } catch (e) {
            console.error(e);
        }
        localLoading.value = false;
    }
    emits("valid");
};

const previousEvent = async () => {
    if (props.actions?.action?.previous && props.actions?.action?.previous instanceof Function) {
        localLoading.value = true;
        try {
            await props.actions?.action.previous();
        } catch (e) {
            console.error(e);
        }
        localLoading.value = false;
    }
    emits("previous");
};

const afterEvent = () => {
    emits("after");
};

// ---- SLOT PARAMS ----

const activatorSlotParams = computed(() => {
    return {
        onClick: () => {
            _dialog.value = true;
        },
    };
});

const actionSlotParams = computed(() => {
    return {
        onClick: validEvent,
        disabled: dialogActionDisabled,
    };
});

const closeDialog = () => {
    _dialog.value = false;
    snackbarDialog.value = false;
    emits("close", false);
};

const reduceDialog = () => {
    snackbarDialog.value = true;
};

const mountDialog = () => {
    snackbarDialog.value = false;
};

const afterTransition = () => {
    emits("afterEnter");
};

return (_ctx: any,_cache: any) => {
  const _component_v_btn_ternary = _resolveComponent("v-btn-ternary")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_btn_primary = _resolveComponent("v-btn-primary")!
  const _component_v_scroll_y_transition = _resolveComponent("v-scroll-y-transition")!
  const _component_v_hover = _resolveComponent("v-hover")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_btn_icon = _resolveComponent("v-btn-icon")!
  const _component_v_overlay = _resolveComponent("v-overlay")!
  const _component_v_snackbar = _resolveComponent("v-snackbar")!
  const _directive_touch = _resolveDirective("touch")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _renderSlot(_ctx.$slots, "activator", { props: activatorSlotParams.value }),
    _createVNode(_component_v_overlay, {
      modelValue: _dialog.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_dialog).value = $event)),
      width: _unref(mobile) ? '100%' : undefined,
      class: _normalizeClass({ 'align-center': !_unref(mobile), 'align-end': _unref(mobile), 'justify-center': true }),
      onAfterEnter: afterTransition,
      eager: eager.value
    }, {
      default: _withCtx(() => [
        _createVNode(_Transition, { name: _ctx.transition }, {
          default: _withCtx(() => [
            (!_unref(mobile))
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  "data-cy": "oss-dialog-container",
                  class: "d-flex space-x-2 ossDialogContainer",
                  style: _normalizeStyle({
                    width: _ctx.width,
                    height: _ctx.height,
                    maxWidth: _ctx.maxWidth,
                    minWidth: _ctx.minWidth,
                    padding: '4px',
                })
                }, [
                  _createElementVNode("div", {
                    "data-cy": "oss-inner-dialog-container",
                    style: _normalizeStyle({
                        paddingLeft: '56px',
                        width: '100%',
                        minHeight: _ctx.minHeight,
                        maxHeight: _ctx.maxHeight,
                        background: _ctx.bgColor,
                    })
                  }, [
                    _createVNode(_component_v_card, {
                      rounded: "xl",
                      class: "mb-2"
                    }, _createSlots({
                      text: _withCtx(() => [
                        _createElementVNode("div", {
                          class: "overflow-y-auto",
                          style: _normalizeStyle({
                                    maxHeight: hasValidButton.value ? '85vh' : '95vh',
                                })
                        }, [
                          _renderSlot(_ctx.$slots, "default")
                        ], 4)
                      ]),
                      _: 2
                    }, [
                      (!_ctx.withoutHeader)
                        ? {
                            name: "title",
                            fn: _withCtx(() => [
                              _createVNode(_component_v_row, { justify: "space-between" }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_col, { cols: "1" }, {
                                    default: _withCtx(() => [
                                      (hasPreviousButton.value)
                                        ? (_openBlock(), _createBlock(_component_v_btn_ternary, {
                                            key: 0,
                                            onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (previousEvent()), ["stop"])),
                                            disabled: _ctx.previousDisabled,
                                            icon: "$chevron-left",
                                            "data-cy": _ctx.prefixDataCy + '_previous'
                                          }, null, 8, ["disabled", "data-cy"]))
                                        : _createCommentVNode("", true)
                                    ]),
                                    _: 1
                                  }),
                                  _createVNode(_component_v_col, {
                                    cols: "10",
                                    class: "text-center"
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(dialogTitle.value), 1)
                                    ]),
                                    _: 1
                                  }),
                                  _createVNode(_component_v_col, { cols: "1" }, {
                                    default: _withCtx(() => [
                                      _renderSlot(_ctx.$slots, "after", {}, () => [
                                        (hasAfterButton.value)
                                          ? (_openBlock(), _createBlock(_component_v_btn, {
                                              key: 0,
                                              onClick: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (afterEvent()), ["stop"])),
                                              disabled: _ctx.afterDisabled,
                                              icon: "$chevron_right"
                                            }, null, 8, ["disabled"]))
                                          : _createCommentVNode("", true)
                                      ])
                                    ]),
                                    _: 3
                                  })
                                ]),
                                _: 3
                              })
                            ]),
                            key: "0"
                          }
                        : undefined
                    ]), 1024),
                    _renderSlot(_ctx.$slots, "actions", { props: actionSlotParams.value }, () => [
                      (hasValidButton.value)
                        ? (_openBlock(), _createBlock(_component_v_btn_primary, {
                            key: 0,
                            block: "",
                            size: "x-large",
                            onClick: validEvent,
                            disabled: dialogDisabled.value,
                            loading: loading.value || _ctx.validLoading,
                            "data-cy": _ctx.prefixDataCy + '_action',
                            class: _normalizeClass({
                                error: _ctx.error,
                            })
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(dialogActionTitle.value), 1)
                            ]),
                            _: 1
                          }, 8, ["disabled", "loading", "data-cy", "class"]))
                        : _createCommentVNode("", true)
                    ])
                  ], 4),
                  _createVNode(_component_v_hover, null, {
                    default: _withCtx(({ isHovering, props }) => [
                      _createElementVNode("div", _mergeProps({ class: "rounded-pill oss-dialog-close ml-2" }, props), [
                        _createVNode(_component_v_btn, {
                          icon: "$oss-close",
                          onClick: _cache[2] || (_cache[2] = ($event: any) => (closeDialog())),
                          color: "white",
                          variant: "flat",
                          size: "large",
                          rounded: "pill",
                          "data-cy": _ctx.prefixDataCy + '_close'
                        }, null, 8, ["data-cy"]),
                        _createVNode(_component_v_scroll_y_transition, null, {
                          default: _withCtx(() => [
                            isHovering
                              ? (_openBlock(), _createBlock(_component_v_btn, {
                                  key: 0,
                                  icon: "$fa-minus",
                                  onClick: _cache[3] || (_cache[3] = ($event: any) => (reduceDialog())),
                                  color: "white",
                                  variant: "flat",
                                  size: "large",
                                  rounded: "pill",
                                  "data-cy": _ctx.prefixDataCy + '_minus'
                                }, null, 8, ["data-cy"]))
                              : _createCommentVNode("", true)
                          ]),
                          _: 2
                        }, 1024)
                      ], 16)
                    ]),
                    _: 1
                  })
                ], 4))
              : (_openBlock(), _createElementBlock("div", _hoisted_1, [
                  _createVNode(_component_v_card, {
                    "min-height": _ctx.minHeight,
                    height: _ctx.height,
                    class: "pa-2",
                    style: {
                        width: '100%',
                    }
                  }, {
                    default: _withCtx(() => [
                      _withDirectives((_openBlock(), _createBlock(_component_v_row, { "no-gutters": "" }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_col, { cols: "4" }),
                          _createVNode(_component_v_col, {
                            cols: "4",
                            class: "d-flex align-center"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_divider)
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_col, {
                            cols: "4",
                            class: "d-flex justify-end align-center pa-2"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_btn_icon, {
                                elevation: "0",
                                size: "small",
                                icon: "$oss-close",
                                onClick: closeDialog
                              })
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })), [
                        [_directive_touch, {
                            down: () => (_dialog.value = false),
                        }]
                      ]),
                      _createElementVNode("div", _hoisted_2, _toDisplayString(dialogTitle.value), 1),
                      _createElementVNode("div", _hoisted_3, [
                        _renderSlot(_ctx.$slots, "default")
                      ]),
                      _renderSlot(_ctx.$slots, "actions", { props: actionSlotParams.value }, () => [
                        (hasValidButton.value)
                          ? (_openBlock(), _createBlock(_component_v_btn_primary, {
                              key: 0,
                              block: "",
                              size: "x-large",
                              onClick: validEvent,
                              disabled: dialogDisabled.value,
                              loading: loading.value || _ctx.validLoading,
                              "data-cy": _ctx.prefixDataCy + '_action',
                              class: _normalizeClass({
                                error: _ctx.error,
                            })
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(dialogActionTitle.value), 1)
                              ]),
                              _: 1
                            }, 8, ["disabled", "loading", "data-cy", "class"]))
                          : _createCommentVNode("", true)
                      ])
                    ]),
                    _: 3
                  }, 8, ["min-height", "height"])
                ]))
          ]),
          _: 3
        }, 8, ["name"])
      ]),
      _: 3
    }, 8, ["modelValue", "width", "class", "eager"]),
    _createVNode(_component_v_snackbar, {
      modelValue: snackbarDialog.value,
      "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((snackbarDialog).value = $event)),
      timeout: -1,
      color: "white"
    }, {
      actions: _withCtx(() => [
        _createElementVNode("div", _hoisted_5, [
          _createVNode(_component_v_btn_icon, {
            size: "24",
            icon: "$add",
            color: "secondary",
            onClick: _cache[5] || (_cache[5] = ($event: any) => (mountDialog()))
          }),
          _createVNode(_component_v_btn_icon, {
            size: "24",
            icon: "$close",
            color: "secondary",
            onClick: _cache[6] || (_cache[6] = ($event: any) => (closeDialog()))
          })
        ])
      ]),
      default: _withCtx(() => [
        _createElementVNode("span", _hoisted_4, _toDisplayString(dialogTitle.value), 1)
      ]),
      _: 1
    }, 8, ["modelValue"])
  ], 64))
}
}

})